export type Predicate<Context> = (context: Context) => boolean;

export function not<Context>(predicate: Predicate<Context>): Predicate<Context> {
  return (context) => !predicate(context);
}

export function and<Context>(...predicates: Predicate<Context>[]): Predicate<Context> {
  return (context) => predicates.every((predicate) => predicate(context));
}

export function or<Context>(...predicates: Predicate<Context>[]): Predicate<Context> {
  return (context) => predicates.some((predicate) => predicate(context));
}

export type BaseContext = {
  flagStore: { ready: boolean; flags: Record<string, boolean | undefined> };
  startDate?: unknown;
  paidReportIssues?: boolean;
};

export function requireFeatureFlag(featureFlag: string): Predicate<BaseContext> {
  return ({ flagStore }) => Boolean(flagStore.ready && flagStore.flags[featureFlag]);
}

export function omitWhenFeatureFlag(featureFlag: string): Predicate<BaseContext> {
  return ({ flagStore }) => Boolean(flagStore.ready && !flagStore.flags[featureFlag]);
}

type PredicateItem<Context> = { predicate?: Predicate<Context> };

export function predicateFilter<Context, Item extends PredicateItem<Context>>(
  items: readonly Item[] | null | undefined,
  context: Context,
): Item[];

export function predicateFilter<Context, Item>(
  items: readonly Item[] | null | undefined,
  context: Context,
  mapper: (item: Item) => PredicateItem<Context>,
): Item[];

export function predicateFilter<Context, Item>(
  items: readonly Item[] | null | undefined,
  context: Context,
  mapper?: (item: Item) => PredicateItem<Context>,
): Item[] {
  return (items ?? []).filter((item) => {
    const { predicate } = mapper ? mapper(item) : (item as PredicateItem<Context>);

    return !predicate || predicate(context);
  });
}

export function if_else<Context, Value>(
  ...args: [...Array<[predicate: Predicate<Context>, value: Value]>, elseValue: Value]
): (context: Context) => Value {
  return (context: Context) => {
    const branches = args.slice(0, -1) as Array<[predicate: Predicate<Context>, value: Value]>;

    for (const [predicate, value] of branches) {
      if (predicate(context)) return value;
    }

    return args.at(-1)! as Value;
  };
}
